import type { Tables } from '../../types/database.types';

export type OrderAsset = Tables<'order_assets'>;
export type AssetDecision = Tables<'asset_decisions'>;

/**
 * What TAP can see of a client's approval, per component.
 *
 * Until now: nothing. No screen in the app read order_assets, asset_decisions
 * or signoff_certificates — the only place a decision was ever displayed was
 * the client's own portal page. So the person who has to act on a rejection
 * could not see that one had happened, what the client objected to, or which
 * version they were looking at.
 *
 * This module is the reading half of that. It is deliberately pure: the panel
 * that renders it is small and dull, and every judgement about what a state
 * means lives here where it can be tested.
 */

/** The four slots, and what the client has done with each. */
export interface ComponentRow {
  component: string;
  /** The live asset, or null when nothing has ever been uploaded for this slot. */
  asset: OrderAsset | null;
  status: ComponentStatus;
  version: number;
  /** The client's own words on the most recent revision request. */
  note: string;
  decidedAt: string | null;
  /** Whether TAP can upload a replacement into this slot right now. */
  canReupload: boolean;
}

export type ComponentStatus =
  'missing' | 'awaiting_client' | 'changes_requested' | 'approved' | 'signed_off';

/**
 * A live asset is the one the client is looking at.
 *
 * `superseded` rows are history — every re-upload leaves one behind, and the
 * partial unique index in the database guarantees at most one live row per
 * component. Picking the highest version among the live states rather than the
 * highest version overall keeps this honest if that ever changes.
 */
const LIVE_STATES = new Set(['draft', 'in_review', 'approved', 'final_approved', 'rejected']);

function liveAssetFor(component: string, assets: OrderAsset[]): OrderAsset | null {
  const live = assets
    .filter((asset) => asset.component_type === component && LIVE_STATES.has(asset.state))
    .sort((a, b) => b.version - a.version);
  return live[0] ?? null;
}

function statusOf(asset: OrderAsset | null): ComponentStatus {
  if (asset === null) {
    return 'missing';
  }
  switch (asset.state) {
    case 'approved':
      return 'approved';
    case 'final_approved':
      return 'signed_off';
    case 'rejected':
      return 'changes_requested';
    default:
      // draft and in_review are the same fact to somebody at TAP: the client
      // has it and has not answered. The distinction only ever mattered to the
      // portal, which shows both as pending anyway.
      return 'awaiting_client';
  }
}

/**
 * The most recent thing the client said about this component.
 *
 * Decisions are append-only and a component can be sent back more than once,
 * so the note shown is the newest revision request — an older one is answered
 * history and showing it would misdirect whoever is fixing the artwork.
 */
function latestNote(
  asset: OrderAsset | null,
  decisions: AssetDecision[],
): { note: string; decidedAt: string | null } {
  if (asset === null) {
    return { note: '', decidedAt: null };
  }
  const mine = decisions
    .filter((decision) => decision.asset_id === asset.id)
    .sort((a, b) => b.decided_at.localeCompare(a.decided_at));
  const newest = mine[0];
  if (newest === undefined) {
    return { note: '', decidedAt: null };
  }
  return { note: newest.note, decidedAt: newest.decided_at };
}

/**
 * Can a replacement be uploaded into this slot?
 *
 * Mirrors upload_order_asset exactly (20261004000100): it refuses a component
 * whose live row is approved or final_approved with `component_already_approved`.
 * A slot the client has settled is settled — the client agreed to those exact
 * bytes and the certificate refers to them.
 *
 * A missing slot is uploadable: that is the repair path for an order whose
 * proofs never fully uploaded.
 */
export function canReupload(status: ComponentStatus): boolean {
  return status === 'changes_requested' || status === 'missing' || status === 'awaiting_client';
}

export function componentRows(
  components: string[],
  assets: OrderAsset[],
  decisions: AssetDecision[],
): ComponentRow[] {
  return components.map((component) => {
    const asset = liveAssetFor(component, assets);
    const status = statusOf(asset);
    const { note, decidedAt } = latestNote(asset, decisions);
    return {
      component,
      asset,
      status,
      version: asset?.version ?? 0,
      note,
      decidedAt,
      canReupload: canReupload(status),
    };
  });
}

/** Plain-language label for a slot's state. */
export function statusLabel(status: ComponentStatus): string {
  switch (status) {
    case 'missing':
      return 'Not uploaded';
    case 'awaiting_client':
      return 'With client';
    case 'changes_requested':
      return 'Changes requested';
    case 'approved':
      return 'Approved';
    case 'signed_off':
      return 'Signed off';
  }
}

export function statusTone(
  status: ComponentStatus,
): 'neutral' | 'warning' | 'revision' | 'success' {
  switch (status) {
    case 'missing':
      return 'neutral';
    case 'awaiting_client':
      return 'warning';
    case 'changes_requested':
      return 'revision';
    case 'approved':
    case 'signed_off':
      return 'success';
  }
}

/**
 * One sentence for the top of the panel.
 *
 * The count that matters is how many the client has settled, not how many rows
 * exist — an order with four uploaded proofs and no answers is at the start of
 * the process, not the end.
 */
export function approvalSummary(rows: ComponentRow[]): string {
  const total = rows.length;
  const settled = rows.filter(
    (row) => row.status === 'approved' || row.status === 'signed_off',
  ).length;
  const rejected = rows.filter((row) => row.status === 'changes_requested').length;

  if (rows.every((row) => row.status === 'signed_off')) {
    return 'Signed off by the client.';
  }
  if (rejected > 0) {
    return `${String(rejected)} of ${String(total)} sent back for changes — upload a new version to return it to the client.`;
  }
  if (settled === total && total > 0) {
    return 'All components approved. The client can now sign off.';
  }
  return `${String(settled)} of ${String(total)} approved.`;
}
